'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

interface ScheduleModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const ScheduleModal: React.FC<ScheduleModalProps> = ({ isOpen, onClose }) => {
    const [showForm, setShowForm] = useState(false); // Toggle between options and form

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        mobile: '',
        company: '',
        domain: '',
        service: '',
        budget: '',
        overview: ''
    });
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            // Delay reset to allow exit animation to finish
            const timer = setTimeout(() => {
                setShowForm(false);
                setSubmitStatus('idle');
                setErrors({});
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    }, [isOpen]);

    const serviceOptions = [
        'Website Development',
        'Mobile App Development',
        'Brand Visibility (SEO/GBP)',
        'E-commerce Development',
        'UI/UX Design',
        'Complete Digital Package',
        'Other'
    ];

    const budgetOptions = [
        '₹30,000 - ₹50,000',
        '₹50,000 - ₹1,00,000',
        '₹1,00,000 - ₹2,50,000',
        '₹2,50,000 - ₹5,00,000',
        '₹5,00,000+',
        'Lets discuss in connect'
    ];

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email';
        }
        if (!formData.mobile.trim()) {
            newErrors.mobile = 'Mobile number is required';
        } else if (!/^[+]?[0-9]{10,15}$/.test(formData.mobile.replace(/\s/g, ''))) {
            newErrors.mobile = 'Please enter a valid mobile number';
        }
        if (!formData.service) newErrors.service = 'Please select a service';
        if (!formData.budget) newErrors.budget = 'Please select a budget range';
        if (!formData.overview.trim()) {
            newErrors.overview = 'Project overview is required';
        } else if (formData.overview.trim().length < 20) {
            newErrors.overview = 'Please provide at least 20 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        setSubmitStatus('idle');

        try {
            const response = await fetch('https://sitemapgenius.com/backend/contact.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    mobile: formData.mobile,
                    company: formData.company,
                    website: formData.domain,
                    service: formData.service,
                    budget: formData.budget,
                    message: formData.overview
                }),
            });

            const result = await response.json();

            if (result.success) {
                setSubmitStatus('success');
                setFormData({
                    name: '', email: '', mobile: '', company: '', domain: '', service: '', budget: '', overview: ''
                });
                setTimeout(() => {
                    setSubmitStatus('idle');
                    onClose(); // Close modal on success
                }, 3000);
            } else {
                setSubmitStatus('error');
                console.error('Submission failed:', result.message);
            }
        } catch (error) {
            setSubmitStatus('error');
            console.error('Submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const meetingOptions = [
        {
            id: 'call',
            title: 'Book a 30m Intro Call',
            desc: 'Discuss your project goals',
            icon: 'CalendarDaysIcon',
            action: 'Book Now',
            onClick: () => setShowForm(true) // Switch to form view
        },
        {
            id: 'whatsapp',
            title: 'Chat on WhatsApp',
            desc: 'Get instant answers',
            icon: 'ChatBubbleLeftRightIcon',
            action: 'Chat',
            href: 'https://wa.me/918686284396?text=Hi%20SitemapGenius,%20I%27m%20interested%20in%20starting%20a%20project.'
        },
        {
            id: 'email',
            title: 'Send an Email',
            desc: 'Request a proposal',
            icon: 'EnvelopeIcon',
            action: 'Email',
            href: 'mailto:sitemapgenius@gmail.com'
        }
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100]"
                    />

                    {/* Box/Sheet */}
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 300 }}
                        className="fixed bottom-0 left-0 right-0 z-[101] bg-white rounded-t-[2rem] shadow-2xl overflow-hidden border-t border-slate-100 max-h-[85vh] flex flex-col"
                    >
                        {/* Drag Handle Area - Fixed at top */}
                        <motion.div
                            className="w-full py-6 flex justify-center cursor-grab active:cursor-grabbing touch-none bg-white z-10"
                            drag="y"
                            dragConstraints={{ top: 0, bottom: 0 }}
                            dragElastic={{ top: 0, bottom: 0.8 }}
                            onDragEnd={(_, info) => {
                                if (info.offset.y > 50) onClose();
                            }}
                        >
                            <div className="w-16 h-1.5 bg-slate-200 rounded-full" />
                        </motion.div>

                        {/* Scrollable Content Area */}
                        <div className="overflow-y-auto flex-1 px-8 pb-12">
                            {!showForm ? (
                                /* OPTIONS VIEW */
                                <>
                                    <div className="text-center mb-8">
                                        <h3 className="text-2xl font-bold text-slate-900 mb-2">Schedule Your Meeting</h3>
                                        <p className="text-slate-500">Pick the best way to connect with our team.</p>
                                    </div>

                                    <div className="space-y-4">
                                        {meetingOptions.map((option) => (
                                            <div
                                                key={option.id}
                                                onClick={() => option.onClick ? option.onClick() : window.open(option.href, option.id === 'whatsapp' ? '_blank' : '_self')}
                                                className="flex items-center p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-indigo-100 hover:shadow-md transition-all group cursor-pointer"
                                            >
                                                <div className="w-12 h-12 rounded-full bg-white border border-slate-100 flex items-center justify-center group-hover:bg-indigo-50 group-hover:text-indigo-600 transition-colors">
                                                    <Icon name={option.icon as any} className="w-6 h-6 text-slate-600 group-hover:text-indigo-600" />
                                                </div>
                                                <div className="ml-4 flex-1">
                                                    <h4 className="font-bold text-slate-900 group-hover:text-indigo-700 transition-colors">{option.title}</h4>
                                                    <p className="text-sm text-slate-500">{option.desc}</p>
                                                </div>
                                                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white opacity-0 group-hover:opacity-100 transition-all transform translate-x-2 group-hover:translate-x-0 shadow-sm text-indigo-600">
                                                    <Icon name="ArrowRightIcon" className="w-4 h-4" />
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <button
                                        onClick={onClose}
                                        className="mt-8 w-full py-4 text-slate-400 font-medium hover:text-slate-600 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                /* FORM VIEW */
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                >
                                    <div className="flex items-center mb-6">
                                        <button
                                            onClick={() => setShowForm(false)}
                                            className="p-2 rounded-full hover:bg-slate-100 mr-2 transition-colors"
                                        >
                                            <Icon name="ArrowLeftIcon" className="w-5 h-5 text-slate-500" />
                                        </button>
                                        <h3 className="text-xl font-bold text-slate-900">Project Details</h3>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Name <span className="text-red-500">*</span></label>
                                                <input
                                                    type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name"
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-slate-300'}`}
                                                />
                                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Email <span className="text-red-500">*</span></label>
                                                <input
                                                    type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address"
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
                                                />
                                                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Mobile <span className="text-red-500">*</span></label>
                                                <input
                                                    type="tel" name="mobile" value={formData.mobile} onChange={handleChange} placeholder="+91 9999999999"
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${errors.mobile ? 'border-red-500' : 'border-slate-300'}`}
                                                />
                                                {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Domain (Optional)</label>
                                                <input
                                                    type="text" name="domain" value={formData.domain} onChange={handleChange} placeholder="yourwebsite.com"
                                                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Service <span className="text-red-500">*</span></label>
                                                <select
                                                    name="service" value={formData.service} onChange={handleChange}
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white ${errors.service ? 'border-red-500' : 'border-slate-300'}`}
                                                >
                                                    <option value="">Select Service</option>
                                                    {serviceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                                {errors.service && <p className="text-xs text-red-500 mt-1">{errors.service}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Budget <span className="text-red-500">*</span></label>
                                                <select
                                                    name="budget" value={formData.budget} onChange={handleChange}
                                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white ${errors.budget ? 'border-red-500' : 'border-slate-300'}`}
                                                >
                                                    <option value="">Select Budget</option>
                                                    {budgetOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                                </select>
                                                {errors.budget && <p className="text-xs text-red-500 mt-1">{errors.budget}</p>}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">Project Overview <span className="text-red-500">*</span></label>
                                            <textarea
                                                name="overview" value={formData.overview} onChange={handleChange} rows={4} placeholder="Describe your project..."
                                                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none ${errors.overview ? 'border-red-500' : 'border-slate-300'}`}
                                            />
                                            {errors.overview && <p className="text-xs text-red-500 mt-1">{errors.overview}</p>}
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full bg-indigo-600 text-white font-bold py-4 px-6 rounded-lg hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg hover:shadow-indigo-500/30"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Icon name="ArrowPathIcon" className="w-5 h-5 animate-spin" />
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    Submit Request
                                                    <Icon name="PaperAirplaneIcon" className="w-5 h-5" />
                                                </>
                                            )}
                                        </button>

                                        {submitStatus === 'success' && (
                                            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
                                                <Icon name="CheckCircleIcon" className="w-6 h-6" />
                                                <span className="font-medium">Request Submitted! We'll contact you shortly.</span>
                                            </div>
                                        )}
                                    </form>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ScheduleModal;
